import { useState } from "react";
import { ExternalLink } from "lucide-react";

import { postAPIPayload } from "../../apis/postApi";
import { cn } from "../../lib/utils";
import { servicerEpAwareApi } from "../for-service-provider/servicerEpAwareApi";

type ServicerEpAwareDashboardButtonProps = {
  isSidebarOpen?: boolean;
  onNavigate?: () => void;
};

export const ServicerEpAwareDashboardButton = ({
  isSidebarOpen = true,
  onNavigate,
}: ServicerEpAwareDashboardButtonProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenDashboard = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await postAPIPayload({
        url: servicerEpAwareApi.dashboardSso,
        data: {},
        token: true,
      });

      const dashboardUrl = response?.data?.dashboard_url;
      if (response?.success && typeof dashboardUrl === "string" && dashboardUrl.trim()) {
        window.open(dashboardUrl, "_blank", "noopener,noreferrer");
        onNavigate?.();
        return;
      }

      setError(response?.message || "Unable to open EpAware dashboard.");
    } catch {
      setError("Unable to open EpAware dashboard.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <li>
      {error && isSidebarOpen ? (
        <p className="px-3 pb-1 text-xs text-destructive">{error}</p>
      ) : null}
      <button
        type="button"
        onClick={handleOpenDashboard}
        disabled={loading}
        title={error || undefined}
        className={cn(
          "flex items-start gap-3 rounded-lg transition-colors w-full text-left",
          "text-muted-foreground hover:bg-muted hover:text-foreground",
          loading && "opacity-70 cursor-wait",
          isSidebarOpen
            ? "px-3 py-2.5 text-sm font-medium min-w-0"
            : "px-0 py-2.5 justify-center",
        )}
      >
        <ExternalLink className="h-5 w-5 shrink-0 mt-0.5" />
        {isSidebarOpen ? (
          <span className="flex-1 min-w-0 whitespace-normal break-words leading-5">
            {loading ? "Opening EpAware…" : "EpAware ID validations"}
          </span>
        ) : null}
      </button>
    </li>
  );
};
